import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalStore } from '@/core/store';
import { api } from '@/core/http';
import { eventBus } from '@/core/event-bus';

export function Login() {
  const navigate = useNavigate();
  const { setUser, setToken } = useGlobalStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post<{
        token: string;
        user: {
          id: number;
          username: string;
          email?: string;
          role: string;
        };
      }>('/auth/login', {
        username,
        password,
      });

      if (response.code === 0 && response.data) {
        const { token, user } = response.data;
        setToken(token);
        setUser({
          id: user.id,
          username: user.username,
          email: user.email || `${user.username}@redmix.com`,
          role: user.role as any,
        });
        
        eventBus.emit('toast:show', {
          message: '登录成功',
          type: 'success',
        });
        
        navigate('/dashboard');
      } else {
        setError(response.message || '登录失败');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#E85A24] to-[#FF7A45] rounded-xl flex items-center justify-center relative overflow-hidden">
              <div className="absolute w-5 h-5 bg-white/90 rounded-full top-2 left-2" />
              <div className="absolute w-2.5 h-2.5 bg-white/50 rounded-full bottom-2.5 right-2.5" />
            </div>
            <span className="text-3xl font-serif tracking-tight text-gray-900 dark:text-white">
              RedMix
            </span>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            欢迎回来
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            登录您的账户以继续使用
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                用户名
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E85A24] focus:border-transparent transition-all"
                placeholder="请输入用户名"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                密码
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E85A24] focus:border-transparent transition-all"
                placeholder="请输入密码"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-br from-[#E85A24] to-[#FF7A45] text-white font-medium rounded-lg hover:from-[#FF7A45] hover:to-[#FF8533] focus:outline-none focus:ring-2 focus:ring-[#E85A24] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {loading ? '登录中...' : '登录'}
            </button>
          </form>
        </div>

        {/* Back to Landing */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-[#E85A24] transition-colors"
          >
            ← 返回首页
          </button>
        </div>
      </div>
    </div>
  );
}

